import { IconButton, Paper, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/styles';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Link,
  Redirect,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import foundersAPI, { Founder, FounderStatus } from '../../api/founders';
import {
  Button,
  ConfirmButton,
  PageLoader,
  RouteACL,
  SnackMessage,
} from '../../components/common';
import DeleteButton from '../../components/common/delete-modal';
import EmptyDetails from '../../components/common/empty-details';
import EnrichProfileModal from '../../components/common/enrich-profile-modal';
import FounderInviteModal from '../../components/common/founder-invite-modal';
import PageTabs from '../../components/founders/page-tabs';
import BaseLayout from '../../components/layout/base-layout';
import { Header } from '../../components/layout/main-layout';
import {
  FounderContext,
  FounderProvider,
} from '../../contexts/founder-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import FounderAdditionalInfoPage from './founder-additional-info-page';
import FounderDetailsPage from './founder-details-page';
import FounderLogPage from './founder-log-page';
import FounderNotesPage from './founder-notes-page';
import FounderVenturesPage from './founder-ventures-page';

const useStyles = makeStyles({
  container: {
    flexGrow: 1,
    padding: 0,
  },
  emptyContainer: {
    flexGrow: 1,
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  content: {
    padding: 40,
  },
  requestLink: {
    minWidth: 187,
    whiteSpace: 'nowrap',
  },
  actionsContainer: {
    display: 'flex',
    columnGap: '16px',
  },
});

function FounderPage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const { founderId } = useParams<{ founderId: Founder['id'] }>();
  const location = useLocation<{
    prevPath?: string;
    openEnrichModal?: boolean;
  }>();
  const { enqueueSnackbar } = useSnackbar();

  const { hasAccessToAction } = useContext(UserContext);

  const [prevPath] = useState(location.state?.prevPath);
  const {
    founder,
    isFounderLoading,
    founderSpecializations,
    loadFounder,
    loadFounderSpecializations,
    updateFounder,
    isAccessDenied,
  } = useContext(FounderContext);
  const [sending, setSending] = useState(false);
  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);
  const [isEnrichModalOpen, setIsEnrichModalOpen] = useState(false);

  useEffect(() => {
    if (location.state?.openEnrichModal) {
      setIsEnrichModalOpen(true);
    }
  }, [location.state?.openEnrichModal]);

  const handleCloseEnrichModal = useCallback(() => {
    setIsEnrichModalOpen(false);
  }, []);

  const handleEnrichSuccess = useCallback(
    (updatedFounder: Founder) => {
      updateFounder(updatedFounder);
      loadFounderSpecializations(founderId);
    },
    [updateFounder, loadFounderSpecializations, founderId],
  );

  const handleStatusChange = useCallback(
    async (status: FounderStatus) => {
      if (founder) {
        const prevStatus = founder.status;
        try {
          updateFounder({ ...founder, status });
          await foundersAPI.update(founder.id, { ...founder, status });
          enqueueSnackbar(
            `The founder status was successfully changed to '${status}'`,
            {
              variant: 'success',
            },
          );
        } catch (e: any) {
          const messageError = e.response?.data?.message;

          updateFounder({ ...founder, status: prevStatus });
          enqueueSnackbar(
            "An error occurred while changing the founder's status. Please, try again.",
            {
              content: (key, message) =>
                SnackMessage({
                  key,
                  message,
                  variant: 'error',
                  additionalMessage: messageError,
                }),
              variant: 'error',
            },
          );
          console.error('error status change', e);
        }
      }
    },
    [founder, updateFounder, enqueueSnackbar],
  );

  const handleSendFounderUpdate = async (onClose: () => any) => {
    try {
      setSending(true);
      await foundersAPI.sendFounderUpdateInvites([founderId]);
      enqueueSnackbar('The profile update request was successfully sent', {
        variant: 'success',
      });
      setSending(false);
      onClose();
    } catch (e) {
      const messageError = (e as AxiosError).response?.data?.message;

      setSending(false);
      enqueueSnackbar(
        'An error occurred while sending profile update request. Please, try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
      throw e;
    }
  };

  // load founder's info
  useEffect(() => {
    if (!founder) {
      loadFounder(founderId);
    }
  }, [founderId, founder, loadFounder]);

  // load founder's specializations
  useEffect(() => {
    if (!founderSpecializations) {
      loadFounderSpecializations(founderId);
    }
  }, [founderSpecializations, founderId, loadFounderSpecializations]);

  const backLink = useMemo(() => {
    return (
      <Tooltip title='Back to Founders'>
        <IconButton
          component={Link}
          to={prevPath || Pages.ACTIVE_FOUNDERS}
          data-testid='button-back-founders'
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
    );
  }, [prevPath]);

  return (
    <BaseLayout user={user} fullHeight>
      {isFounderLoading ? (
        <PageLoader />
      ) : isAccessDenied ? (
        <EmptyDetails
          backLink={backLink}
          title='Access Denied'
          content='Unfortunately you do not have access to this page. Please contact program admin if you believe this is a mistake'
        />
      ) : !founder ? (
        <EmptyDetails backLink={backLink} />
      ) : (
        <>
          <Header
            title={`${founder?.firstName} ${founder?.lastName}`}
            onStatusChange={handleStatusChange}
            backLink={backLink}
            isMergedActions
            actions={[
              {
                key: 'send-invite',
                label: 'Send Invite',
                hidden: !hasAccessToAction('userManagement.founder'),
                component: (
                  <Button
                    onClick={() => setIsOpenInviteModal(true)}
                    variant='contained'
                    data-testid='button-invite-user'
                  >
                    Send Invite
                  </Button>
                ),
              },
              {
                key: 'send-profile-update',
                label: 'Request Profile Update',
                hidden: !hasAccessToAction('founder.updateProfile'),
                component: (
                  <ConfirmButton
                    title='Send Single Profile Update Request'
                    loading={sending}
                    body='Your are about to send a temporary link to allow participants to update their profile. The link can work only 1 time, and is available for 48 hours only.'
                    successProps={{
                      btnLabel: 'Ok',
                      onSuccess: handleSendFounderUpdate,
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    buttonRenderer={({ onClick }) => (
                      <Button
                        className={classes.requestLink}
                        onClick={onClick}
                        variant='contained'
                        data-testid='button-invite-founder-update'
                      >
                        Request Profile Update
                      </Button>
                    )}
                  />
                ),
              },
              {
                key: 'enrich-profile',
                label: 'Enrich Profile',
                hidden: !hasAccessToAction('founder.updateProfile'),
                component: (
                  <Button
                    onClick={() => setIsEnrichModalOpen(true)}
                    variant='contained'
                    data-testid='button-enrich-profile'
                  >
                    Enrich Profile
                  </Button>
                ),
              },
              {
                key: 'delete',
                label: 'Delete',
                hidden: !hasAccessToAction('founder.delete'),
                component: <DeleteButton type='founder' id={founderId} />,
              },
            ]}
          />
          <Paper className={classes.container} elevation={1}>
            <PageTabs
              founderId={founderId}
              userAssigned={founder?.userAssigned}
            />
            <div className={classes.content}>
              <Switch>
                <RouteACL
                  exact
                  path={Pages.FOUNDER_DETAILS}
                  component={FounderDetailsPage}
                  rule='founder.details.view'
                  redirectUrl={Pages.ACTIVE_FOUNDERS}
                />
                <RouteACL
                  exact
                  path={Pages.FOUNDER_VENTURES}
                  component={FounderVenturesPage}
                  rule='founder.ventures.view'
                  redirectUrl={Pages.ACTIVE_FOUNDERS}
                />
                <RouteACL
                  exact
                  path={Pages.FOUNDER_ADDITIONAL_INFO}
                  component={FounderAdditionalInfoPage}
                  rule='founder.additionalInfo.view'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                  attribute='customQuestionsFounderAdditionalInfoEnabled'
                />
                <RouteACL
                  exact
                  path={Pages.FOUNDER_NOTES}
                  component={FounderNotesPage}
                  rule='founder.notes.view'
                  redirectUrl={Pages.ACTIVE_FOUNDERS}
                />
                <RouteACL
                  exact
                  path={Pages.FOUNDER_LOG}
                  component={FounderLogPage}
                  rule='founder.logs.view'
                  redirectUrl={Pages.ACTIVE_FOUNDERS}
                />
                <Redirect to={Pages.FOUNDER_DETAILS} />
              </Switch>
            </div>
          </Paper>
          {hasAccessToAction('userManagement.mentor') && (
            <FounderInviteModal
              open={isOpenInviteModal}
              founderId={founderId}
              onClose={() => setIsOpenInviteModal(false)}
            />
          )}
          <EnrichProfileModal
            open={isEnrichModalOpen}
            onClose={handleCloseEnrichModal}
            type='founder'
            entityId={founderId}
            onSuccess={handleEnrichSuccess}
          />
        </>
      )}
    </BaseLayout>
  );
}

function withFounderProvider<P>(Component: React.FunctionComponent<P>) {
  return (props: P) => (
    <FounderProvider>
      <Component {...props} />
    </FounderProvider>
  );
}

export default withFounderProvider(FounderPage);
